import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Stack,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  useTheme,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { fetchJson } from '../utils/fetchJson';
import { BROCHURE, brochureGradient } from '../utils/brandPalette.generated';
import { ensureBrandingFontsLoaded } from '../utils/brandingFonts';
// The Android app, offered wherever a visitor might look for it. Every one of
// these renders nothing until a system administrator has said where the app is.
import { AndroidAppButton, AndroidAppQr, useAndroidApp } from '../components/AndroidAppDownload';
import HelpCenterPage from './School_Admin/Support/HelpCenterPage';
import {
  School as SchoolIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  AccountBalanceWallet as FinanceIcon,
  AppRegistration as RegisterIcon,
  Menu as MenuIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Class as ClassIcon,
  Badge as BadgeIcon,
  Business as FacilitiesIcon,
  Parent as ParentIcon,
  SupervisedUserCircle as StaffIcon,
  MenuBook as BookIcon,
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  Science as ScienceIcon,
  Psychology as BrainIcon,
  Lightbulb as IdeaIcon,
  Security as SecurityIcon,
  Payments as PaymentsIcon,
  AccountBalance as PayrollIcon,
  FactCheck as AttendanceIcon,
  Language as LanguageIcon,
  Backup as BackupIcon,
  NotificationsActive as NotificationsIcon,
  Call as PhoneIcon,
  Email as EmailIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

/**
 * THE LANDING PAGE HAS ITS OWN THEME, and does not take the school's.
 *
 * This page is the front of the product. It renders before anyone has signed
 * in — before there IS a school — so the palette in force was whatever was left
 * in this browser's localStorage by the last person to use the machine. A demo
 * account on Dark Mode made the public front page dark for the next visitor,
 * who had chosen nothing and had no way to change it back.
 *
 * Most of the page's colour is written into its own sx props and was never at
 * risk. The leaks are the semantic tokens — `bgcolor: 'background.paper'`,
 * `borderColor: 'divider'`, `'primary.main'`, `'grey.100'`, `theme.shadows[n]`
 * — which resolve against whatever ThemeProvider is above them. Pinning the
 * provider fixes every one of those at once, including the ones I would have
 * missed by reading for them.
 *
 * Declared at module scope so it is not rebuilt on every render.
 */
const brochureTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: BROCHURE.ink, dark: BROCHURE.inkDeep, contrastText: BROCHURE.onInk },
    secondary: { main: BROCHURE.gold, dark: BROCHURE.goldDeep, contrastText: BROCHURE.ink },
    background: { default: '#F6F4EC', paper: '#FFFFFF' },
    text: { primary: BROCHURE.onCream, secondary: BROCHURE.onCreamMuted },
    divider: 'rgba(36,28,107,0.14)',
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 8, backgroundColor: '#FFFFFF' } },
    },
  },
});

/**
 * THE PRODUCT, ON THE DEVICES PEOPLE USE IT ON.
 *
 * What stood here was a hand-drawn SVG of a dashboard: invented numbers, a fake
 * bar chart, three fake student rows. It was hidden below md, so more than half
 * of the visitors to this page saw no picture of the product at all — and the
 * half who did saw a drawing of it rather than the thing itself.
 *
 * These are real screenshots, the same ones the module carousel further down
 * the page already uses. A desktop frame and a phone frame, because the product
 * genuinely is both and the brochure shows both.
 *
 * PORTRAIT SHOTS FOR THE PHONE, deliberately. Cropping a 2.2:1 dashboard into a
 * 9:19.5 frame shows a sliver of a toolbar; report.png and the nursery report
 * are 0.77 and 0.84, so they fill it as themselves.
 *
 * The images are decorative — the heading beside them carries the meaning — so
 * they take an empty alt rather than a description a screen reader would have
 * to sit through twice.
 */
// TWO FOLDERS, TWO PURPOSES, and the paths say which is which.
//
//   client/public/modulethumbnails  desktop captures  (~2.18:1)
//   client/public/mobilethumbnails  phone captures    (~0.446, i.e. 9:20.2)
//
// They were briefly in one folder, which is how a 9:20 phone screenshot ends up
// in a carousel built for 2:1 desktop ones and gets centre-cropped to a strip.
// Keeping them apart makes the wrong one obvious in the path itself.
const DESKTOP_DIR = '/modulethumbnails';
const MOBILE_DIR = '/mobilethumbnails';

const DESKTOP_SCREENS = [
  `${DESKTOP_DIR}/School%20Overview.jpg`,
  `${DESKTOP_DIR}/Student%20List.jpg`,
  `${DESKTOP_DIR}/Finance%20Reports.jpg`,
  `${DESKTOP_DIR}/ID%20Cards.jpg`,
];
// ALL TEN, both themes, in the top banner's flipper.
//
// This list was four dark captures, chosen so the frame never cut from a light
// screenshot to a dark one. That reasoning was mine and it was the wrong call:
// the themes ARE the point. An app that follows the device is worth showing, and
// a viewer who sees Encatar in light and then in dark learns something true
// about it rather than watching a frame that refuses to change character.
//
// Interleaved rather than grouped, so a light screen and a dark one are always
// adjacent in the cycle and the alternation reads as deliberate.
//
// The desktop list is four and this is ten; both advance on one counter and each
// wraps independently, so the pairing drifts after the first cycle. That is fine
// here -- the two frames are showing "the same product on both" rather than "the
// same screen on both" -- and it is what letting every capture appear costs.
const PHONE_SCREENS = [
  `${MOBILE_DIR}/mobile6.jpeg`,   // School Overview     dark
  `${MOBILE_DIR}/mobile1.jpeg`,   // Student Directory   light
  `${MOBILE_DIR}/mobile9.jpeg`,   // Class Promotion     dark
  `${MOBILE_DIR}/mobile2.jpeg`,   // ID Card Workshop    light
  `${MOBILE_DIR}/mobile7.jpeg`,   // Financial Reports   dark
  `${MOBILE_DIR}/mobile4.jpeg`,   // Financial Reports   light
  `${MOBILE_DIR}/mobile10.jpeg`,  // Announcements       dark
  `${MOBILE_DIR}/mobile5.jpeg`,   // School Overview     light
  `${MOBILE_DIR}/mobile8.jpeg`,   // School Overview     dark
  `${MOBILE_DIR}/mobile3.jpeg`,   // Report card         light
];

const DeviceShowcase = () => {
  const [frame, setFrame] = useState(0);

  // One timer for both devices, so they change together rather than drifting
  // into an arrhythmia that reads as two broken carousels.
  useEffect(() => {
    const id = setInterval(() => setFrame((n) => n + 1), 4200);
    return () => clearInterval(id);
  }, []);

  const desktopSrc = DESKTOP_SCREENS[frame % DESKTOP_SCREENS.length];
  const phoneSrc = PHONE_SCREENS[frame % PHONE_SCREENS.length];

  return (
    <Box sx={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: { xs: 480, md: 720 }, mx: 'auto', pb: { xs: 11, md: 14 } }}>
      {/* DESKTOP. A browser chrome bar rather than a laptop lid: the lid wastes
          a third of the height on a bezel, and at this size the screenshot is
          the only part worth the pixels. */}
      <Box
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: BROCHURE.inkDeep,
          border: `1px solid ${BROCHURE.rule}`,
          boxShadow: '0 30px 70px rgba(0,0,0,0.42)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1 }}>
          {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
            <Box key={c} sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: c }} />
          ))}
          <Box sx={{ flex: 1 }} />
          <Box sx={{ width: 120, height: 8, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.16)' }} />
          <Box sx={{ flex: 1 }} />
        </Box>
        {/* THE FRAME MATCHES THE SCREENSHOTS, so nothing is cropped.
            A 16:10 frame with objectFit:cover sliced almost a third off the
            sides of these and cut the summary cards in half at both edges. The
            replacement shots are all 1920x880-ish -- 2.17 to 2.19 -- so a frame
            at 2.18 shows them whole. Anything wider than the frame still gets
            centre-cropped rather than squashed. */}
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '2.18 / 1', bgcolor: '#FFFFFF' }}>
          {DESKTOP_SCREENS.map((src) => (
            <Box
              key={src}
              component="img"
              src={src}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top center',
                opacity: src === desktopSrc ? 1 : 0,
                transition: 'opacity 700ms ease',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* PHONE, DROPPED WELL BELOW THE DESKTOP FRAME rather than tucked into
          its corner. Sitting level with the laptop it read as one flat
          collage; carried lower it reads as two devices at different depths,
          which is the only thing giving the group any dimension.
          Still absolutely positioned, so it cannot add to the row's height and
          push the hero copy out of line — the container's padding-bottom grows
          with it instead, because the hero section clips its overflow and the
          phone would otherwise be cut off at the fold. */}
      <Box
        sx={{
          position: 'absolute',
          right: { xs: 2, md: -34 },
          bottom: { xs: -24, md: -40 },
          width: { xs: 124, sm: 152, md: 186 },
          borderRadius: 4,
          p: 0.6,
          bgcolor: '#0B0A1F',
          border: '1px solid rgba(255,255,255,0.22)',
          boxShadow: '0 22px 44px rgba(0,0,0,0.5)',
        }}
      >
        <Box sx={{ position: 'relative', borderRadius: 3.2, overflow: 'hidden', aspectRatio: '482 / 1080', bgcolor: '#0B0A1F' }}>
          {PHONE_SCREENS.map((src) => (
            <Box
              key={src}
              component="img"
              src={src}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top center',
                opacity: src === phoneSrc ? 1 : 0,
                transition: 'opacity 700ms ease',
              }}
            />
          ))}
          {/* NO DRAWN NOTCH. These captures are whole-device screenshots and
              already carry the phone's own status bar -- clock, signal, battery
              -- across the top. A notch painted over that covers the clock with
              a black pill, which looks like a rendering fault rather than a
              phone. The frame supplies the bezel; the screenshot supplies
              everything inside it. */}
        </Box>
      </Box>
    </Box>
  );
};

const FeatureCard = ({ icon: Icon, title, description, features }) => {
  const theme = useTheme();
  const iconColors = ['#241C6B', '#1F5FBF', '#1B3A8F', '#B8862A'];
  const randomColor = iconColors[Math.floor(Math.random() * iconColors.length)];
  
  return (
    <Card 
      component={motion.div}
      whileHover={{ y: -10, boxShadow: theme.shadows[12] }}
      sx={{ 
        height: '100%', 
        borderRadius: 4, 
        transition: '0.3s', 
        display: 'flex', 
        flexDirection: 'column',
        border: '1px solid',
        borderColor: '#DDDAEF',
        bgcolor: '#FFFFFF',
        '&:hover': {
          borderColor: '#1F5FBF'
        }
      }}
    >
      <CardContent sx={{ textAlign: 'center', p: 4, flexGrow: 1 }}>
        <Box sx={{ 
          display: 'inline-flex', 
          p: 2.5, 
          borderRadius: 3, 
          background: `linear-gradient(135deg, ${randomColor} 0%, #1F5FBF 100%)`,
          color: 'white',
          mb: 2,
          boxShadow: 3
        }}>
          <Icon fontSize="large" />
        </Box>
        <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: '#241C6B' }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        <Stack spacing={0.5} sx={{ textAlign: 'left' }}>
          {features.map((f, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <CheckIcon sx={{ fontSize: 16, color: '#1F5FBF' }} />
              <Typography variant="body2">{f}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ModuleThumbnailsCarousel = ({ items }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const scrollerRef = React.useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const cardWidth = 380;
  const cardHeight = 220;
  const gap = 18;

  const dragStateRef = React.useRef({
    isDown: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false
  });

  const scrollToIndex = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const i = Math.max(0, Math.min(idx, (items?.length || 1) - 1));
    const viewport = el.clientWidth || 0;
    const left = i * (cardWidth + gap) - Math.max(0, (viewport - cardWidth) / 2);
    el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    setActiveIndex(i);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => {
      const left = el.scrollLeft;
      const viewport = el.clientWidth || 0;
      const center = left + viewport / 2;
      const i = Math.round((center - cardWidth / 2) / (cardWidth + gap));
      setActiveIndex(Math.max(0, Math.min(i, (items?.length || 1) - 1)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [items?.length]);

  useEffect(() => {
    if (paused) return;
    if (!items || items.length <= 1) return;
    const t = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % items.length;
        scrollToIndex(next);
        return next;
      });
    }, 2600);
    return () => clearInterval(t);
  }, [items, paused]);

  const handleMouseDown = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    dragStateRef.current.isDown = true;
    dragStateRef.current.startX = e.pageX;
    dragStateRef.current.startScrollLeft = el.scrollLeft;
    dragStateRef.current.moved = false;
  };

  const handleMouseMove = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (!dragStateRef.current.isDown) return;
    e.preventDefault();
    const dx = e.pageX - dragStateRef.current.startX;
    if (Math.abs(dx) > 4) dragStateRef.current.moved = true;
    el.scrollLeft = dragStateRef.current.startScrollLeft - dx;
  };

  const handleMouseUpOrLeave = () => {
    dragStateRef.current.isDown = false;
  };

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        position: 'relative',
        borderRadius: 6,
        overflow: 'visible',
        border: '1px solid',
        // THE SAME CREAM THE PRICE CARDS SIT ON. This panel was
        // background.paper — plain white — which put a white slab on the warm
        // off-white page and made the one section carrying the product's own
        // screenshots look like it belonged to a different site. The cards
        // inside stay white, so the screenshots still lift off the ground.
        borderColor: BROCHURE.creamEdge,
        bgcolor: BROCHURE.cream
      }}
    >
      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="overline" color="primary" fontWeight={800} letterSpacing={2}>
              {t('landing.carousel.overline')}
            </Typography>
            <Typography variant="h5" fontWeight={900}>
              {t('landing.carousel.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('landing.carousel.subtitle')}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: 'stretch', md: 'auto' } }}>
            <IconButton
              onClick={() => scrollToIndex(activeIndex - 1)}
              disabled={activeIndex <= 0}
              sx={{
                border: 1,
                borderColor: 'divider',
                bgcolor: '#FFFFFF',
                '&:hover': { bgcolor: BROCHURE.creamEdge }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => scrollToIndex(activeIndex + 1)}
              disabled={activeIndex >= (items?.length || 1) - 1}
              sx={{
                border: 1,
                borderColor: 'divider',
                bgcolor: '#FFFFFF',
                '&:hover': { bgcolor: BROCHURE.creamEdge }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Box
        ref={scrollerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        sx={{
          px: { xs: 2.5, md: 3 },
          pb: { xs: 2.5, md: 3 },
          pt: 4,
          display: 'flex',
          gap: `${gap}px`,
          overflowX: 'auto',
          overflowY: 'visible',
          scrollSnapType: 'x mandatory',
          scrollPaddingLeft: `calc(50% - ${cardWidth / 2}px)`,
          scrollPaddingRight: `calc(50% - ${cardWidth / 2}px)`,
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          cursor: dragStateRef.current.isDown ? 'grabbing' : 'grab',
          userSelect: 'none',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 10 },
          '&::-webkit-scrollbar-thumb': { background: theme.palette.divider, borderRadius: 999 },
          '&::-webkit-scrollbar-track': { background: 'transparent' }
        }}
      >
        {(items || []).map((it, idx) => (
          (() => {
            const dist = Math.abs(idx - activeIndex);
            const isActive = dist === 0;
            const isNeighbor = dist === 1;
            const scale = isActive ? 1.14 : isNeighbor ? 0.98 : 0.9;
            const opacity = isActive ? 1 : isNeighbor ? 0.55 : 0.25;
            const lift = isActive ? -10 : isNeighbor ? -3 : 0;
            const rotateY = isActive ? 0 : (idx < activeIndex ? 10 : -10);

            return (
          <Box
            key={it.src}
            sx={{
              flex: `0 0 ${cardWidth}px`,
              scrollSnapAlign: 'center',
              position: 'relative',
              zIndex: isActive ? 3 : isNeighbor ? 2 : 1,
              cursor: 'pointer'
            }}
            onClick={() => {
              if (dragStateRef.current.moved) return;
              scrollToIndex(idx);
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: 5,
                overflow: 'hidden',
                border: 1,
                borderColor: isActive ? 'primary.main' : 'divider',
                transition: '0.22s ease',
                transform: `perspective(1000px) translateY(${lift}px) rotateY(${rotateY}deg) scale(${scale})`,
                transformOrigin: 'center',
                boxShadow: isActive ? theme.shadows[16] : isNeighbor ? theme.shadows[6] : 'none',
                filter: isActive ? 'none' : dist <= 2 ? 'saturate(0.85)' : 'saturate(0.75) blur(0.4px)',
                opacity,
                bgcolor: 'background.paper'
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: cardHeight,
                  bgcolor: 'grey.50'
                }}
              >
                <Box
                  component="img"
                  src={it.src}
                  alt={it.title}
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    transition: '0.22s ease',
                    transform: isActive ? 'scale(1.03)' : 'scale(1.0)'
                  }}
                />
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography fontWeight={900}>{it.title}</Typography>
                {it.subtitle ? (
                  <Typography variant="caption" color="text.secondary">{it.subtitle}</Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">&nbsp;</Typography>
                )}
              </Box>
            </Paper>
          </Box>
            );
          })()
        ))}
      </Box>

      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ pb: 2 }}>
        {(items || []).map((_, i) => (
          <Box
            key={i}
            onClick={() => scrollToIndex(i)}
            role="button"
            sx={{
              width: i === activeIndex ? 20 : 8,
              height: 8,
              borderRadius: 999,
              bgcolor: i === activeIndex ? 'primary.main' : 'divider',
              transition: '0.2s',
              cursor: 'pointer'
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

const LandingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [branding, setBranding] = useState({
    platformName: '',
    platformLogoUrl: null,
    logoSize: 36,
    platformNameFont: null,
    platformNameColor: null,
    platformNameFontSize: null,
    platformNameFontWeight: null
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Read here as well as inside the buttons so the band above the footer knows
  // whether it has anything to say before it draws itself.
  const androidApp = useAndroidApp();
  const [contactValues, setContactValues] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [contactSnack, setContactSnack] = useState({ open: false, severity: 'success', message: '' });

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const [formValues, setFormValues] = useState({
    name: '',
    address: '',
    adminName: '',
    adminEmail: '',
    adminPhone: '',
    phaseCodes: [],
    region: '',
    town: '',
    founderName: '',
    founderNationalId: '',
    arreteDocuments: []
  });

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const data = await fetchJson('/api/global-settings/branding');
        if (data.success) {
          setBranding({
            platformName: data.platformName || '',
            platformLogoUrl: data.platformLogoUrl,
            logoSize: Number.isFinite(data.logoSize) ? data.logoSize : 36,
            platformNameFont: data.platformNameFont || null,
            platformNameColor: data.platformNameColor || null,
            platformNameFontSize: Number.isFinite(data.platformNameFontSize) ? data.platformNameFontSize : null,
            platformNameFontWeight: data.platformNameFontWeight || null
          });
        }
      } catch (_) {}
    };
    loadBranding();
  }, []);

  useEffect(() => {
    ensureBrandingFontsLoaded([branding.platformNameFont]);
  }, [branding.platformNameFont]);

  const faqs = [
    { key: 'register' },
    { key: 'mobile' },
    { key: 'branches' },
    { key: 'afterSubmit' },
    { key: 'security' }
  ];

  const advantages = [
    { key: 'security', icon: SecurityIcon },
    { key: 'payments', icon: PaymentsIcon },
    { key: 'payroll', icon: PayrollIcon },
    { key: 'attendance', icon: AttendanceIcon },
    { key: 'allInOne', icon: DashboardIcon },
    { key: 'personalization', icon: LanguageIcon },
    { key: 'backups', icon: BackupIcon },
    { key: 'communication', icon: NotificationsIcon }
  ];

  // DESKTOP CAPTURES ONLY, and only files that are on disk.
  //
  // The carousel frames these at a desktop aspect. A phone screenshot dropped
  // into it is 9:20 in a 2:1 window and gets centre-cropped to a vertical
  // strip of itself -- which is what happened when Class Promotion, a phone
  // capture, was in this list. The phone shots have their own home: the device
  // frame in the hero, which is built for their shape.
  //
  // The list used to name sixteen PNGs; the screenshots were replaced with
  // seven JPGs under different names, and fourteen of the sixteen became 404s
  // that the carousel still dutifully scrolled through as broken frames. A
  // gallery is the one component where a missing file is invisible in code and
  // unmissable on screen, so the names are kept exactly as they sit in
  // client/public/modulethumbnails.
  //
  // SPACES ARE ENCODED HERE rather than left to the browser. Most browsers
  // encode a bare space in a src, but a few proxies and every URL parser in
  // between do not have to.
  const moduleGalleryItems = [
    { key: 'schoolOverview', src: `${DESKTOP_DIR}/School%20Overview.jpg` },
    { key: 'studentManagement', src: `${DESKTOP_DIR}/Student%20List.jpg` },
    { key: 'userList', src: `${DESKTOP_DIR}/User%20List.jpg` },
    { key: 'announcements', src: `${DESKTOP_DIR}/Announcement.jpg` },
    { key: 'finance', src: `${DESKTOP_DIR}/Finance.jpg` },
    { key: 'financeReports', src: `${DESKTOP_DIR}/Finance%20Reports.jpg` },
    { key: 'idCards', src: `${DESKTOP_DIR}/ID%20Cards.jpg` }
  ];

  const moduleGalleryItemsTranslated = moduleGalleryItems.map((it) => ({
    ...it,
    title: t(`landing.gallery.${it.key}`)
  }));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactValues(prev => ({ ...prev, [name]: value }));
  };

  const [contactSending, setContactSending] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      const resp = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactValues.name,
          email: contactValues.email,
          phone: contactValues.phone || '',
          subject: contactValues.subject || t('landing.contact.defaultSubject'),
          message: contactValues.message
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        setContactSnack({ open: true, severity: 'success', message: t('landing.contact.success') });
        setContactValues({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setContactSnack({ open: true, severity: 'error', message: data.message || t('landing.contact.error') });
      }
    } catch (_) {
      setContactSnack({ open: true, severity: 'error', message: t('landing.contact.error') });
    } finally {
      setContactSending(false);
    }
  };

  const goToSection = (tabIndex, sectionId) => {
    setActiveTab(tabIndex);
    setMobileNavOpen(false);
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const PHASE_MINISTRY_MAP = {
    nur_pri: { ministry: 'MINEDUB', label: 'MINEDUB (Basic Education)' },
    sec: { ministry: 'MINESEC', label: 'MINESEC (Secondary Education)' }
  };

  const REGION_CITIES = {
    'Adamaoua': ['Ngaoundéré', 'Meiganga', 'Banyo', 'Tibati', 'Tignère', 'Djohong'],
    'Centre': ['Yaoundé', 'Mbalmayo', 'Obala', 'Nanga-Eboko', 'Monatélé', 'Akonolinga', 'Eseka', 'Ntui', 'Mfou', 'Soa'],
    'East': ['Bertoua', 'Batouri', 'Abong-Mbang', 'Yokadouma', 'Belabo', 'Garoua-Boulaï', 'Moloundou'],
    'Far North': ['Maroua', 'Kousséri', 'Mokolo', 'Yagoua', 'Mora', 'Kaélé', 'Maga', 'Guidiguis'],
    'Littoral': ['Douala', 'Nkongsamba', 'Edéa', 'Loum', 'Mbanga', 'Manjo', 'Dizangué', 'Penja'],
    'North': ['Garoua', 'Guider', 'Pitoa', 'Poli', 'Figuil', 'Tchéboa', 'Lagdo'],
    'North-West': ['Bamenda', 'Kumbo', 'Ndop', 'Wum', 'Bali', 'Mbengwi', 'Fundong', 'Nkambe'],
    'South': ['Ebolowa', 'Kribi', 'Sangmélima', 'Ambam', 'Lolodorf', 'Mvangan', 'Campo'],
    'South-West': ['Buea', 'Limbe', 'Kumba', 'Mamfe', 'Tiko', 'Muyuka', 'Mundemba', 'Fontem'],
    'West': ['Bafoussam', 'Dschang', 'Mbouda', 'Foumban', 'Bangangté', 'Bandjoun', 'Foumbot', 'Bafang']
  };
  const REGIONS = Object.keys(REGION_CITIES).sort();
  const availableCities = formValues.region ? (REGION_CITIES[formValues.region] || []) : [];

  const togglePhase = (code) => {
    setFormValues(prev => {
      const wasSelected = prev.phaseCodes.includes(code);
      const nextCodes = wasSelected
        ? prev.phaseCodes.filter(c => c !== code)
        : [...prev.phaseCodes, code];
      const nextArretes = wasSelected
        ? prev.arreteDocuments.filter(d => d.phaseCode !== code)
        : [...prev.arreteDocuments, { phaseCode: code, ministry: PHASE_MINISTRY_MAP[code]?.ministry || '', referenceNumber: '', issueDate: '' }];
      return { ...prev, phaseCodes: nextCodes, arreteDocuments: nextArretes };
    });
  };

  const updateArrete = (phaseCode, field, value) => {
    setFormValues(prev => ({
      ...prev,
      arreteDocuments: prev.arreteDocuments.map(d =>
        d.phaseCode === phaseCode ? { ...d, [field]: value } : d
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Client-side validation
      if (!formValues.name) throw new Error(t('landing.registration.errors.nameRequired', 'School name is required'));
      if (!formValues.phaseCodes || formValues.phaseCodes.length === 0) {
        throw new Error(t('landing.registration.errors.phaseRequired', 'Please select at least one education phase'));
      }
      if (!formValues.founderName) throw new Error(t('landing.registration.errors.founderRequired', 'Founder / Proprietor name is required'));
      const hasArreteRef = formValues.arreteDocuments.some(d => d.referenceNumber && d.referenceNumber.trim());
      if (!hasArreteRef) {
        throw new Error(t('landing.registration.errors.arreteRequired', "At least one Arrêté d'Ouverture reference number is required"));
      }
      if (!formValues.adminName) throw new Error(t('landing.registration.errors.adminNameRequired', 'Admin name is required'));
      if (!formValues.adminEmail) throw new Error(t('landing.registration.errors.adminEmailRequired', 'Admin email is required'));

      const res = await fetchJson('/api/registration/register', {
        method: 'POST',
        body: JSON.stringify(formValues)
      });
      if (res.success) {
        setSuccess(true);
        setFormValues({
          name: '',
          address: '',
          adminName: '',
          adminEmail: '',
          adminPhone: '',
          phaseCodes: [],
          region: '',
          town: '',
          founderName: '',
          founderNationalId: '',
          arreteDocuments: []
        });
      } else {
        throw new Error(res.error || 'Registration failed');
      }
    } catch (err) {
      const backendDetails = err?.data?.details || err?.data?.error;
      setError(backendDetails || err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    { key: 'dashboard', icon: DashboardIcon },
    { key: 'students', icon: PeopleIcon },
    { key: 'academics', icon: AssessmentIcon },
    { key: 'classes', icon: ClassIcon },
    { key: 'staff', icon: StaffIcon },
    { key: 'finance', icon: FinanceIcon },
    { key: 'idCards', icon: BadgeIcon },
    { key: 'facilities', icon: FacilitiesIcon },
    { key: 'portals', icon: RegisterIcon }
  ];

  const handleLanguageChange = (lng) => {
    try { i18n.changeLanguage(lng); } catch (_) {}
  };

  return (
    <ThemeProvider theme={brochureTheme}>
    <Box sx={{ bgcolor: '#F6F4EC', minHeight: '100vh' }}>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#FFFFFF', borderBottom: 1, borderColor: '#DDDAEF' }}>
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              px: { xs: 0 },
              minHeight: { xs: 76, sm: 84, md: 104 },
              py: { xs: 1, md: 1.5 }
            }}
          >
            <Stack
              direction="row"
              spacing={{ xs: 1, sm: 1.25 }}
              alignItems="center"
              sx={{ minWidth: 0, cursor: 'pointer' }}
              onClick={() => goToSection(0, 'features')}
            >
              <Box
                component="img"
                src={
                  branding.platformLogoUrl
                    ? (branding.platformLogoUrl.startsWith('http') ? branding.platformLogoUrl : `${API_BASE}${branding.platformLogoUrl}`)
                    : '/encatarlogo.png'
                }
                alt={branding.platformName}
                sx={{
                  // The size the system admin set, honoured. It used to be
                  // pinned at 72 with a 96 cap, so the setting did nothing here.
                  // Small screens still cap it, because a mark sized for a
                  // desktop bar will not fit a phone one.
                  height: branding.logoSize,
                  maxHeight: { xs: Math.min(branding.logoSize, 40), sm: Math.min(branding.logoSize, 52), md: branding.logoSize },
                  width: 'auto',
                  objectFit: 'contain',
                  transition: 'height 200ms ease',
                }}
              />
              <Typography
                variant="h6"
                color="text.primary"
                sx={{
                  ...(branding.platformNameFont ? { fontFamily: branding.platformNameFont } : {}),
                  ...(branding.platformNameColor ? { color: branding.platformNameColor } : {}),
                  ...(branding.platformNameFontSize ? { fontSize: branding.platformNameFontSize } : { fontSize: { xs: 16, sm: 18, md: 20 } }),
                  ...(branding.platformNameFontWeight ? { fontWeight: branding.platformNameFontWeight } : { fontWeight: 'bold' }),
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: { xs: 140, sm: 220, md: 320 }
                }}
              >
                {branding.platformName}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                sx={{ 
                  mr: 2, 
                  display: { xs: 'none', md: 'flex' },
                  '& .MuiTab-root': { color: '#241C6B' },
                  '& .MuiTab-root.Mui-selected': { color: '#1F5FBF' },
                  '& .MuiTabs-indicator': { bgcolor: '#1F5FBF' }
                }}
              >
                <Tab label={t('landing.nav.features')} />
                <Tab label={t('landing.nav.register')} />
                <Tab label={t('landing.nav.faq')} />
                <Tab label={t('landing.nav.help')} />
              </Tabs>

              <IconButton
                onClick={() => setMobileNavOpen(true)}
                sx={{ display: { xs: 'inline-flex', md: 'none' }, color: '#241C6B' }}
                aria-label={t('landing.aria.openMenu')}
              >
                <MenuIcon />
              </IconButton>

              {/* Beside Login, not instead of it: a visitor arriving on a
                  phone wants the app, one arriving on a laptop wants to sign
                  in, and the bar has room for both from sm up. */}
              <AndroidAppButton
                variant="nav"
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  color: '#241C6B',
                  borderColor: '#241C6B',
                  '&:hover': { borderColor: '#1A1450', bgcolor: 'rgba(36,28,107,0.06)' },
                }}
              />

              <Button
                variant="contained"
                href="/login"
                sx={{
                  bgcolor: '#241C6B',
                  '&:hover': { bgcolor: '#1A1450' },
                  display: { xs: 'none', sm: 'inline-flex' }
                }}
              >
                {t('landing.nav.login')}
              </Button>

              {/* Language toggle, far right. Kept visible at every breakpoint —
                  a visitor who needs the other language needs it on a phone too. */}
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                aria-label={t('landing.footer.language')}
              >
                {['en', 'fr'].map((lng) => {
                  const active = i18n.language === lng || String(i18n.language || '').startsWith(lng);
                  return (
                    <Button
                      key={lng}
                      size="small"
                      onClick={() => handleLanguageChange(lng)}
                      aria-pressed={active}
                      sx={{
                        minWidth: 0,
                        px: { xs: 0.9, sm: 1.2 },
                        py: 0.35,
                        fontSize: { xs: 12, sm: 13 },
                        fontWeight: 700,
                        lineHeight: 1.4,
                        borderRadius: 1,
                        border: 1,
                        borderColor: '#241C6B',
                        color: active ? '#FFFFFF' : '#241C6B',
                        bgcolor: active ? '#241C6B' : 'transparent',
                        '&:hover': {
                          bgcolor: active ? '#1A1450' : 'rgba(26,35,126,0.08)',
                          borderColor: '#1A1450',
                        },
                      }}
                    >
                      {lng.toUpperCase()}
                    </Button>
                  );
                })}
              </Stack>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        PaperProps={{ sx: { width: 300 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography fontWeight={700} sx={{ color: '#241C6B' }}>
            {t('landing.nav.menu')}
          </Typography>
          <IconButton onClick={() => setMobileNavOpen(false)} aria-label={t('landing.aria.closeMenu')}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List disablePadding>
          <ListItemButton onClick={() => { setActiveTab(0); setMobileNavOpen(false); }}>
            <ListItemText primary={t('landing.nav.features')} />
          </ListItemButton>
          <ListItemButton onClick={() => { setActiveTab(1); setMobileNavOpen(false); }}>
            <ListItemText primary={t('landing.nav.register')} />
          </ListItemButton>
          <ListItemButton onClick={() => { setActiveTab(2); setMobileNavOpen(false); }}>
            <ListItemText primary={t('landing.nav.faq')} />
          </ListItemButton>
          <ListItemButton onClick={() => { setActiveTab(3); setMobileNavOpen(false); }}>
            <ListItemText primary={t('landing.nav.help')} />
          </ListItemButton>
          <Box sx={{ px: 2, pt: 2 }}>
            <Button variant="contained" fullWidth href="/login" sx={{ bgcolor: '#241C6B', '&:hover': { bgcolor: '#1A1450' } }}>
              {t('landing.nav.login')}
            </Button>
            {/* The drawer is the phone's whole navigation, and a phone is the
                device that can actually install this. */}
            <AndroidAppButton
              variant="nav"
              fullWidth
              onOpen={() => setMobileNavOpen(false)}
              sx={{ mt: 1.25, color: '#241C6B', borderColor: '#241C6B' }}
            />
          </Box>
        </List>
      </Drawer>

      {/* Hero Section - Only shown for Features tab */}
      {/* THE HERO GROUND IS OURS NOW.
          It used to hotlink a stock illustration from img.freepik.com and lay
          two gradients over it. That is a third party on the critical path of
          our own front page: it is a request we do not control, it is the
          slowest thing on the page, it fails to a bare indigo panel behind a
          corporate proxy, and the licence for a hotlinked freepik asset is
          not ours to rely on. The brochure's own gradient replaces it, with a
          soft gold glow where the stock art's warmth was. */}
      {activeTab === 0 && (
        <Box sx={{
          background: brochureGradient(165),
          color: BROCHURE.onInk,
          py: { xs: 8, md: 12 },
          position: 'relative',
          zIndex: 0,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              `radial-gradient(60% 55% at 82% 8%, rgba(223,174,67,0.20) 0%, rgba(223,174,67,0) 62%),`
              + ` radial-gradient(50% 50% at 8% 96%, rgba(31,95,191,0.28) 0%, rgba(31,95,191,0) 66%)`,
            zIndex: 0,
            pointerEvents: 'none',
          },
        }}>
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={5}>
                <motion.div
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  {/* THE BROCHURE'S OWN OPENING, in its own order: the trial
                      first, then the promise, then the claim. Gold is used here
                      and almost nowhere else on the page, which is what makes it
                      read as an accent rather than as a second brand colour. */}
                  <Box
                    sx={{
                      display: 'inline-block',
                      mb: 2,
                      px: 1.75,
                      py: 0.6,
                      borderRadius: 999,
                      bgcolor: BROCHURE.gold,
                      color: BROCHURE.ink,
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      letterSpacing: '0.12em',
                    }}
                  >
                    {t('landing.hero.badge')}
                  </Box>
                  <Typography variant="h2" fontWeight="bold" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                    {t('landing.hero.title')}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      mb: 2,
                      color: BROCHURE.gold,
                      fontWeight: 700,
                      fontStyle: 'italic',
                      fontSize: { xs: '1.05rem', md: '1.25rem' },
                    }}
                  >
                    {t('landing.hero.tagline')}
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 2.5, opacity: 0.9 }}>
                    {t('landing.hero.subtitle', { platformName: branding.platformName })}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2.5, opacity: 0.9, maxWidth: 620 }}>
                    {t('landing.hero.body')}
                  </Typography>
                  <Typography
                    sx={{
                      mb: 4,
                      color: BROCHURE.onInkMuted,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      fontSize: '0.9rem',
                    }}
                  >
                    {t('landing.hero.proof', { modules: modules.length })}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button 
                      variant="contained" 
                      size="large" 
                      onClick={() => setActiveTab(1)}
                      sx={{
                        // GOLD ON THE INDIGO GROUND. This button was indigo on
                        // indigo -- the same colour as the panel behind it, so
                        // the primary call to action was the least visible
                        // thing in the hero.
                        bgcolor: BROCHURE.gold,
                        color: BROCHURE.ink,
                        '&:hover': { bgcolor: BROCHURE.goldDeep, color: BROCHURE.onInk },
                        px: 4,
                        py: 1.5,
                        fontWeight: 'bold'
                      }}
                    >
                      {t('landing.hero.primaryCta')}
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="large" 
                      onClick={() => setActiveTab(0)}
                      sx={{ 
                        color: 'white', 
                        borderColor: 'white', 
                        px: 4,
                        '&:hover': {
                          borderColor: '#1F5FBF',
                          bgcolor: 'rgba(31, 95, 191, 0.1)'
                        }
                      }}
                    >
                      {t('landing.hero.secondaryCta')}
                    </Button>
                  </Stack>
                </motion.div>
              </Grid>
              {/* SHOWN ON PHONES TOO. This was display:none below md, so the
                  visitors most likely to be on a phone saw a page that never
                  showed them the product. */}
              <Grid item xs={12} md={7}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <DeviceShowcase />
                </motion.div>
              </Grid>
            </Grid>

            <Box sx={{ mt: { xs: 4, md: 6 }, position: 'relative', zIndex: 2 }}>
              <Paper elevation={0} sx={{
                borderRadius: 4,
                border: '2px solid rgba(255,255,255,0.5)',
                bgcolor: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                p: { xs: 3, md: 4 },
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #241C6B 0%, #1F5FBF 100%)',
                    p: 1.5,
                    borderRadius: 2,
                    display: 'inline-flex'
                  }}>
                    <IdeaIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Typography variant="h5" fontWeight={900} sx={{ color: '#241C6B' }}>
                    {t('landing.subscription.title')}
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.8, mb: 3 }}>
                  <Trans
                    i18nKey="landing.subscription.body"
                    components={{ strong: <strong style={{ color: '#1F5FBF' }} /> }}
                  />
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setActiveTab(1)}
                    sx={{ 
                      fontWeight: 'bold',
                      bgcolor: '#241C6B',
                      '&:hover': { bgcolor: '#1A1450' },
                      px: 4,
                      py: 1.5
                    }}
                  >
                    {t('landing.subscription.primaryCta')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => setActiveTab(0)}
                    sx={{ 
                      fontWeight: 'bold',
                      borderColor: '#1F5FBF',
                      color: '#1F5FBF',
                      px: 4,
                      '&:hover': {
                        borderColor: '#241C6B',
                        bgcolor: 'rgba(31, 95, 191, 0.05)'
                      }
                    }}
                  >
                    {t('landing.subscription.secondaryCta')}
                  </Button>
                </Stack>
              </Paper>
            </Box>
          </Container>
        </Box>
      )}

      {activeTab === 0 && (
        <Container maxWidth="xl" disableGutters sx={{ mt: { xs: -7, md: -9 }, mb: 4, px: { xs: 1.5, md: 3 }, position: 'relative', zIndex: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ModuleThumbnailsCarousel items={moduleGalleryItemsTranslated} />
          </motion.div>
        </Container>
      )}

      {activeTab === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Features Section */}
          <Container id="features" maxWidth="lg" sx={{ py: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="overline" sx={{ color: '#1F5FBF', fontWeight: 'bold', letterSpacing: 2 }}>
                {t('landing.features.overline')}
              </Typography>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                {t('landing.features.title')}
              </Typography>
              <Divider sx={{ width: 80, height: 4, background: 'linear-gradient(90deg, #241C6B 0%, #1F5FBF 100%)', mx: 'auto', mt: 2, borderRadius: 2 }} />
            </Box>
            <Grid container spacing={4}>
              {modules.map((module, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <FeatureCard
                    icon={module.icon}
                    title={t(`landing.modules.${module.key}.title`)}
                    description={t(`landing.modules.${module.key}.description`)}
                    features={t(`landing.modules.${module.key}.features`, { returnObjects: true })}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 12 }}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="overline" sx={{ color: '#1F5FBF', fontWeight: 'bold', letterSpacing: 2 }}>
                  {t('landing.advantage.overline')}
                </Typography>
                <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                  {t('landing.advantage.title')}
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 860, mx: 'auto' }}>
                  {t('landing.advantage.subtitle', { platformName: branding.platformName })}
                </Typography>
                <Divider sx={{ width: 80, height: 4, background: 'linear-gradient(90deg, #241C6B 0%, #1F5FBF 100%)', mx: 'auto', mt: 2, borderRadius: 2 }} />
              </Box>

              <Grid container spacing={3}>
                {advantages.map((a, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Paper
                      component={motion.div}
                      whileHover={{ y: -8 }}
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 4,
                        border: 2,
                        borderColor: '#DDDAEF',
                        bgcolor: '#FFFFFF',
                        height: '100%',
                        transition: '0.25s',
                        '&:hover': {
                          borderColor: '#1F5FBF',
                          boxShadow: theme.shadows[10]
                        }
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 3,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            background: 'linear-gradient(135deg, #241C6B 0%, #1F5FBF 100%)',
                            boxShadow: 3
                          }}
                        >
                          <a.icon />
                        </Box>
                        <Typography fontWeight={900} sx={{ color: '#241C6B' }}>
                          {t(`landing.advantage.items.${a.key}.title`)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          {t(`landing.advantage.items.${a.key}.description`)}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ mt: 10 }}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="overline" sx={{ color: '#1F5FBF', fontWeight: 'bold', letterSpacing: 2 }}>
                  {t('landing.pricing.overline')}
                </Typography>
                <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                  {t('landing.pricing.title')}
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 820, mx: 'auto' }}>
                  {t('landing.pricing.subtitle')}
                </Typography>
                <Divider sx={{ width: 80, height: 4, background: 'linear-gradient(90deg, #241C6B 0%, #1F5FBF 100%)', mx: 'auto', mt: 2, borderRadius: 2 }} />
              </Box>

              {/* THE BROCHURE'S PANEL, not three white cards on white.
                  The brochure prints its prices on the indigo ground with cream
                  cards and one gold figure; this section was the last part of
                  the page still in the old blue-on-white. The middle card is
                  the one carrying a number, so it is the one that takes gold —
                  and it is the only gold on the section, which is what keeps it
                  reading as the answer rather than as decoration. */}
              <Box
                sx={{
                  background: brochureGradient(160),
                  borderRadius: 5,
                  p: { xs: 3, md: 5 },
                  boxShadow: '0 24px 60px rgba(26,20,80,0.28)',
                }}
              >
                <Grid container spacing={{ xs: 2.5, md: 4 }} alignItems="stretch">
                  {/* THE BROCHURE'S SCHEME, in the brochure's order: the trial,
                      then the fee, then a person to ask.
                      It prints "3 MONTHS FREE TRIAL" and, beneath it, "then one
                      simple fee per academic year" -- and deliberately no
                      figure. So there is no figure here either. The page had
                      been showing a bare "1000" hardcoded in the JSX; publishing
                      a number the brochure withholds would have the sales
                      conversation contradicted by the website before it starts. */}
                  {[
                    { key: 'trial', highlight: false },
                    { key: 'fee', highlight: true },
                    { key: 'quote', highlight: false },
                  ].map(({ key, highlight }) => (
                    <Grid item xs={12} md={4} key={key}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 3, md: 4 },
                          borderRadius: 4,
                          height: '100%',
                          bgcolor: BROCHURE.cream,
                          border: '2px solid',
                          borderColor: highlight ? BROCHURE.gold : BROCHURE.creamEdge,
                          // The priced card sits slightly proud of the other
                          // two, which is how the brochure separates them
                          // without a second colour.
                          boxShadow: highlight ? '0 16px 40px rgba(0,0,0,0.28)' : 'none',
                        }}
                      >
                        <Typography variant="h6" fontWeight={900} gutterBottom sx={{ color: BROCHURE.onCream }}>
                          {t(`landing.pricing.cards.${key}.title`)}
                        </Typography>

                        {/* THE FIGURE CARRIES ITS CURRENCY AND ITS PERIOD.
                            It read "1000" — bare, hardcoded in the JSX, no unit
                            and no period. A number with no currency beside it is
                            not a price; it is a number, and a visitor deciding
                            whether they can afford this cannot use it. It now
                            comes from the translation file, so the French page
                            can space it the way French does. */}
                        <Typography
                          fontWeight={900}
                          sx={{
                            mb: 1.25,
                            color: highlight ? BROCHURE.goldDeep : BROCHURE.onCream,
                            // The fee is the line the eye should land on, so it
                            // is the only one at display size. The other two
                            // are answers, not headlines.
                            fontSize: highlight ? '1.9rem' : '1.15rem',
                            letterSpacing: '-0.01em',
                          }}
                        >
                          {t(`landing.pricing.cards.${key}.amount`)}
                        </Typography>

                        <Typography sx={{ color: BROCHURE.onCreamMuted, lineHeight: 1.8 }}>
                          {t(`landing.pricing.cards.${key}.body`)}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Typography
                  sx={{
                    mt: { xs: 3, md: 4 },
                    textAlign: 'center',
                    color: BROCHURE.gold,
                    fontWeight: 700,
                  }}
                >
                  {t('landing.pricing.demo')}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  onClick={() => setActiveTab(1)} 
                  sx={{ 
                    px: 5, 
                    py: 1.6, 
                    fontWeight: 'bold',
                    bgcolor: BROCHURE.gold,
                    color: BROCHURE.ink,
                    '&:hover': { bgcolor: BROCHURE.goldDeep, color: BROCHURE.onInk }
                  }}
                >
                  {t('landing.pricing.primaryCta')}
                </Button>
              </Box>
            </Box>

            <Box id="contact" sx={{ mt: 12 }}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="overline" sx={{ color: '#1F5FBF', fontWeight: 'bold', letterSpacing: 2 }}>
                  {t('landing.contact.overline')}
                </Typography>
                <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                  {t('landing.contact.title')}
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 860, mx: 'auto' }}>
                  {t('landing.contact.subtitle')}
                </Typography>
                <Divider sx={{ width: 80, height: 4, background: 'linear-gradient(90deg, #241C6B 0%, #1F5FBF 100%)', mx: 'auto', mt: 2, borderRadius: 2 }} />
              </Box>

              <Paper elevation={0} sx={{ borderRadius: 6, border: 2, borderColor: '#DDDAEF', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
                <Grid container>
                  <Grid item xs={12} md={5} sx={{
                    p: { xs: 4, md: 5 },
                    bgcolor: '#241C6B',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(135deg, rgba(3,169,244,0.25) 0%, rgba(255,255,255,0.0) 65%)'
                    }
                  }}>
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Typography variant="h5" fontWeight={900} sx={{ mb: 1.5 }}>
                        {t('landing.contact.infoTitle')}
                      </Typography>
                      <Typography sx={{ opacity: 0.92, lineHeight: 1.8, mb: 3 }}>
                        {t('landing.contact.infoSubtitle')}
                      </Typography>

                      <Stack spacing={2.25}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 3,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(255,255,255,0.14)'
                          }}>
                            <PhoneIcon />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ opacity: 0.85 }}>
                              {t('landing.contact.phoneLabel')}
                            </Typography>
                            <Typography fontWeight={800}>
                              +237683888573
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 3,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(255,255,255,0.14)'
                          }}>
                            <EmailIcon />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ opacity: 0.85 }}>
                              {t('landing.contact.emailLabel')}
                            </Typography>
                            <Typography fontWeight={800}>
                              encatar80@gmail.com
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 5 } }}>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="h5" fontWeight={900} sx={{ color: '#241C6B' }}>
                        {t('landing.contact.formTitle')}
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        {t('landing.contact.formSubtitle')}
                      </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleContactSubmit}>
                      <Grid container spacing={2.25}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            name="name"
                            label={t('landing.contact.fields.name')}
                            value={contactValues.name}
                            onChange={handleContactChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            name="email"
                            type="email"
                            label={t('landing.contact.fields.email')}
                            value={contactValues.email}
                            onChange={handleContactChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            name="phone"
                            label={t('landing.contact.fields.phone')}
                            value={contactValues.phone}
                            onChange={handleContactChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            name="subject"
                            label={t('landing.contact.fields.subject')}
                            value={contactValues.subject}
                            onChange={handleContactChange}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            required
                            name="message"
                            label={t('landing.contact.fields.message')}
                            value={contactValues.message}
                            onChange={handleContactChange}
                            multiline
                            minRows={4}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={contactSending}
                            endIcon={contactSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                            sx={{
                              bgcolor: '#241C6B',
                              '&:hover': { bgcolor: '#1A1450' },
                              px: 4,
                              py: 1.4,
                              fontWeight: 900
                            }}
                          >
                            {contactSending ? t('landing.contact.sending', { defaultValue: 'Sending...' }) : t('landing.contact.submit')}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Container>
        </motion.div>
      )}

      {activeTab === 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Registration Section */}
          <Box id="register" sx={{ bgcolor: '#F6F4EC', py: 10 }}>
            <Container maxWidth="md">
              <Paper elevation={0} sx={{ p: { xs: 4, md: 6 }, borderRadius: 6, border: 2, borderColor: '#DDDAEF', bgcolor: '#FFFFFF' }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                    {t('landing.registration.title')}
                  </Typography>
                  <Typography color="text.secondary">
                    {t('landing.registration.subtitle')}
                  </Typography>
                </Box>

                {success ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CheckIcon sx={{ fontSize: 80, color: '#1F5FBF', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                      {t('landing.registration.successTitle')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 4 }}>
                      {t('landing.registration.successBody')}
                    </Typography>
                    <Button 
                      variant="contained" 
                      onClick={() => setSuccess(false)}
                      sx={{
                        bgcolor: '#241C6B',
                        '&:hover': { bgcolor: '#1A1450' }
                      }}
                    >
                      {t('landing.registration.newRegistration')}
                    </Button>
                  </Box>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('landing.registration.sections.schoolInfo')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('landing.registration.fields.schoolName')}
                          name="name"
                          required
                          value={formValues.name}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('landing.registration.fields.address')}
                          name="address"
                          multiline
                          rows={2}
                          value={formValues.address}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('landing.registration.fields.phases')}</Typography>
                        <Stack direction="row" spacing={2}>
                          <FormControlLabel
                            control={<Checkbox checked={formValues.phaseCodes.includes('nur_pri')} onChange={() => togglePhase('nur_pri')} />}
                            label={t('landing.registration.phaseOptions.nurPri')}
                          />
                          <FormControlLabel
                            control={<Checkbox checked={formValues.phaseCodes.includes('sec')} onChange={() => togglePhase('sec')} />}
                            label={t('landing.registration.phaseOptions.sec')}
                          />
                        </Stack>
                      </Grid>

                      {/* Location */}
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel>{t('landing.registration.fields.region', 'Region')}</InputLabel>
                          <Select
                            value={formValues.region}
                            label={t('landing.registration.fields.region', 'Region')}
                            onChange={(e) => setFormValues(prev => ({ ...prev, region: e.target.value, town: '' }))}
                          >
                            {REGIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required disabled={!formValues.region}>
                          <InputLabel>{t('landing.registration.fields.town', 'Town / City')}</InputLabel>
                          <Select
                            value={formValues.town}
                            label={t('landing.registration.fields.town', 'Town / City')}
                            onChange={(e) => setFormValues(prev => ({ ...prev, town: e.target.value }))}
                          >
                            {availableCities.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* School Credentials Section */}
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {t('landing.registration.sections.credentials', 'School Credentials')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('landing.registration.credentialsHint', 'Provide the following to verify school authenticity.')}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('landing.registration.fields.founderName', 'Founder / Proprietor Name')}
                          name="founderName"
                          required
                          value={formValues.founderName}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('landing.registration.fields.founderNationalId', 'Founder National ID (CNI)')}
                          name="founderNationalId"
                          value={formValues.founderNationalId}
                          onChange={handleInputChange}
                          helperText={t('landing.registration.fields.founderNationalIdHint', 'Optional but recommended for verification')}
                        />
                      </Grid>

                      {/* Arrêté d'Ouverture per phase */}
                      {formValues.phaseCodes.length > 0 && (
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            {t('landing.registration.sections.arrete', "Arrêté d'Ouverture (Opening Order)")}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('landing.registration.arreteHint', 'Provide the opening order reference for each education phase your school operates.')}
                          </Typography>
                          {formValues.phaseCodes.map(code => {
                            const info = PHASE_MINISTRY_MAP[code];
                            const doc = formValues.arreteDocuments.find(d => d.phaseCode === code) || {};
                            const phaseLabel = code === 'nur_pri'
                              ? t('landing.registration.phaseOptions.nurPri', 'Nursery & Primary')
                              : t('landing.registration.phaseOptions.sec', 'Secondary');
                            return (
                              <Box key={code} sx={{ mb: 2, p: 2, bgcolor: '#F6F4EC', borderRadius: 2, border: '1px solid #DDDAEF' }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1.5 }}>
                                  {phaseLabel} — {info?.label || code}
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label={t('landing.registration.fields.arreteRef', 'Reference Number')}
                                      required
                                      value={doc.referenceNumber || ''}
                                      onChange={(e) => updateArrete(code, 'referenceNumber', e.target.value)}
                                      helperText={t('landing.registration.fields.arreteRefHint', 'e.g. N° 1234/MINEDUB/...')}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label={t('landing.registration.fields.arreteDate', 'Issue Date')}
                                      type="date"
                                      InputLabelProps={{ shrink: true }}
                                      value={doc.issueDate || ''}
                                      onChange={(e) => updateArrete(code, 'issueDate', e.target.value)}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            );
                          })}
                        </Grid>
                      )}

                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('landing.registration.sections.adminContact')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('landing.registration.fields.adminName')}
                          name="adminName"
                          required
                          value={formValues.adminName}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('landing.registration.fields.adminEmail')}
                          name="adminEmail"
                          type="email"
                          required
                          value={formValues.adminEmail}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('landing.registration.fields.adminPhone')}
                          name="adminPhone"
                          value={formValues.adminPhone}
                          onChange={handleInputChange}
                        />
                      </Grid>

                      <Grid item xs={12} sx={{ mt: 2 }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <Button
                          fullWidth
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={loading}
                          sx={{ 
                            py: 2, 
                            fontWeight: 'bold', 
                            fontSize: '1.1rem',
                            bgcolor: '#241C6B',
                            '&:hover': { bgcolor: '#1A1450' },
                            '&:disabled': { bgcolor: '#E0E0E0' }
                          }}
                        >
                          {loading ? <CircularProgress size={24} color="inherit" /> : t('landing.registration.submit')}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Paper>
            </Container>
          </Box>
        </motion.div>
      )}

      {activeTab === 2 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* FAQ Section */}
          <Container maxWidth="md" sx={{ py: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                {t('landing.faq.title')}
              </Typography>
              <Typography color="text.secondary">
                {t('landing.faq.subtitle')}
              </Typography>
            </Box>
            <Stack spacing={2}>
              {faqs.map((faq, index) => (
                <Paper key={index} elevation={0} sx={{ p: 3, border: 2, borderColor: '#DDDAEF', borderRadius: 2, bgcolor: '#FFFFFF' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                    {t(`landing.faq.items.${faq.key}.q`)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t(`landing.faq.items.${faq.key}.a`)}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Container>
        </motion.div>
      )}

      {activeTab === 3 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Help Center Section */}
          <Container maxWidth="lg" sx={{ py: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#241C6B' }}>
                {t('landing.help.title')}
              </Typography>
              <Typography color="text.secondary">
                {t('landing.help.subtitle')}
              </Typography>
            </Box>
            <Box sx={{ border: 2, borderColor: '#DDDAEF', borderRadius: 4, overflow: 'hidden', bgcolor: '#FFFFFF' }}>
              <HelpCenterPage />
            </Box>
          </Container>
        </motion.div>
      )}

      {/* THE APP, ON THE WAY TO THE FOOTER.
          A band rather than a section with its own heading in the nav: it is a
          way of using the product, not a sixth thing the product does. Placed
          last because a visitor who has read this far is the one deciding how
          they will use it — and it disappears entirely when no link is set. */}
      {!!androidApp.url && (
        <Box sx={{ background: brochureGradient(105), color: BROCHURE.onInk, py: { xs: 6, md: 7 } }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 3, md: 6 }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  component="img"
                  src="/applogo.png"
                  alt=""
                  sx={{
                    width: { xs: 56, md: 72 },
                    height: { xs: 56, md: 72 },
                    borderRadius: 3,
                    objectFit: 'contain',
                    bgcolor: 'rgba(255,255,255,0.9)',
                    p: 1,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={800}>
                    {t('landing.androidBand.title', {
                      platformName: branding.platformName,
                      defaultValue: `${branding.platformName} on your phone`,
                    })}
                  </Typography>
                  <Typography sx={{ mt: 0.75, color: BROCHURE.onInkMuted, maxWidth: 560 }}>
                    {t('landing.androidBand.body', {
                      defaultValue: 'Registers, results, fees and school messages in your pocket — for teachers, parents and school staff alike.',
                    })}
                  </Typography>
                  {!!androidApp.version && (
                    <Typography variant="caption" sx={{ color: BROCHURE.onInkMuted, display: 'block', mt: 1 }}>
                      {t('androidApp.version', {
                        version: androidApp.version,
                        defaultValue: `Version ${androidApp.version}`,
                      })}
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Stack direction="row" spacing={2.5} alignItems="center" sx={{ flexShrink: 0 }}>
                <AndroidAppButton
                  variant="hero"
                  sx={{
                    bgcolor: BROCHURE.gold,
                    color: BROCHURE.ink,
                    px: 4,
                    py: 1.5,
                    fontWeight: 'bold',
                    '&:hover': { bgcolor: BROCHURE.goldDeep, color: BROCHURE.onInk },
                  }}
                />
                {/* Beside the button, not in front of it. A visitor on a laptop
                    cannot install an Android app on the laptop, and this is how
                    it reaches the phone in their pocket without them having to
                    type a URL into it. */}
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <AndroidAppQr />
                </Box>
              </Stack>
            </Stack>
          </Container>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ bgcolor: '#FFFFFF', py: 7, borderTop: 2, borderColor: '#DDDAEF' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  component="img"
                  src={
                    branding.platformLogoUrl
                      ? (branding.platformLogoUrl.startsWith('http') ? branding.platformLogoUrl : `${API_BASE}${branding.platformLogoUrl}`)
                      : '/encatarlogo.png'
                  }
                  alt={branding.platformName}
                  sx={{ height: Math.round(branding.logoSize * 1.25), width: 'auto', objectFit: 'contain' }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    ...(branding.platformNameFont ? { fontFamily: branding.platformNameFont } : {}),
                    ...(branding.platformNameColor ? { color: branding.platformNameColor } : {}),
                    ...(branding.platformNameFontSize ? { fontSize: branding.platformNameFontSize } : {}),
                    ...(branding.platformNameFontWeight ? { fontWeight: branding.platformNameFontWeight } : { fontWeight: 'bold' })
                  }}
                >
                  {branding.platformName}
                </Typography>
              </Stack>
              <Typography color="text.secondary" variant="body2" sx={{ lineHeight: 1.8 }}>
                {t('landing.footer.tagline')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography fontWeight={900} sx={{ color: '#241C6B', mb: 1 }}>
                {t('landing.footer.quickLinks')}
              </Typography>
              <Stack spacing={0.5}>
                <Button size="small" variant="text" sx={{ justifyContent: 'flex-start', px: 0 }} onClick={() => goToSection(0, 'features')}>
                  {t('landing.footer.links.features')}
                </Button>
                <Button size="small" variant="text" sx={{ justifyContent: 'flex-start', px: 0 }} onClick={() => goToSection(0, 'contact')}>
                  {t('landing.footer.links.contact')}
                </Button>
                <Button size="small" variant="text" sx={{ justifyContent: 'flex-start', px: 0 }} onClick={() => goToSection(1, 'register')}>
                  {t('landing.footer.links.register')}
                </Button>
                <Button size="small" variant="text" sx={{ justifyContent: 'flex-start', px: 0 }} onClick={() => goToSection(2, 'faq')}>
                  {t('landing.footer.links.faq')}
                </Button>
                <Button size="small" variant="text" sx={{ justifyContent: 'flex-start', px: 0 }} onClick={() => goToSection(3, 'help')}>
                  {t('landing.footer.links.help')}
                </Button>
                <Button size="small" variant="text" sx={{ justifyContent: 'flex-start', px: 0 }} href="/terms">
                  {t('landing.footer.links.terms')}
                </Button>
                <AndroidAppButton variant="text" />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography fontWeight={900} sx={{ color: '#241C6B', mb: 1 }}>
                {t('landing.footer.contact')}
              </Typography>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneIcon sx={{ color: '#1F5FBF' }} />
                  <Typography variant="body2" color="text.secondary">
                    +237683888573
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailIcon sx={{ color: '#1F5FBF' }} />
                  <Typography variant="body2" color="text.secondary">
                    encatar80@gmail.com
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('landing.footer.copyright', { year: new Date().getFullYear(), platformName: branding.platformName })}
            </Typography>
            {/* Language switching now lives in the top nav bar. */}
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Button size="small" variant="text" href="/login" sx={{ px: 0 }}>
                {t('landing.footer.login')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Snackbar open={contactSnack.open} autoHideDuration={4500} onClose={() => setContactSnack(prev => ({ ...prev, open: false }))}>
        <Alert onClose={() => setContactSnack(prev => ({ ...prev, open: false }))} severity={contactSnack.severity}>
          {contactSnack.message}
        </Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
    </Box>
    </ThemeProvider>
  );
};

export default LandingPage;
